import { useState, useRef, useEffect } from 'react';
import { theme as T } from '../../theme';

/**
 * Shared user profile block: avatar (initials), optionally name/role/chevron, dropdown with Logout.
 * Use in Sidebar footer or as TopBar rightAddon (e.g. mobile reports).
 *
 * @param {Object} user - { initials, name, role, onLogout }
 * @param {boolean} [compact] - Slightly smaller for TopBar (e.g. mobile)
 * @param {boolean} [iconOnly] - Show only the avatar circle; name, role, and chevron hidden. Dropdown still opens on click.
 */
export default function UserProfileDropdown({
  user,
  compact = false,
  iconOnly = false,
  inline = false,
}: {
  user: any;
  compact?: boolean;
  iconOnly?: boolean;
  inline?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [open]);

  if (!user) return null;

  const size = compact || iconOnly ? 26 : 28;
  const nameSize = compact ? 11 : 12;
  const roleSize = compact ? 9 : 10;

  return (
    <div
      ref={ref}
      style={{
        position: 'relative',
        display: inline ? 'flex' : 'block',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen(!open)}
        onKeyDown={(e) => e.key === 'Enter' && setOpen(!open)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: compact ? 8 : 10,
          cursor: 'pointer',
          borderRadius: 8,
          padding: compact || iconOnly ? '2px 0' : '4px 0',
          minWidth: 0,
        }}
      >
        <div
          style={{
            width: size,
            height: size,
            borderRadius: size / 2,
            background: T.hover,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: compact || iconOnly ? 11 : 12,
            fontWeight: 600,
            color: T.t2,
            flexShrink: 0,
          }}
        >
          {user.initials}
        </div>
        {!iconOnly && (
          <>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: nameSize,
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: T.t1,
                }}
              >
                {user.name}
              </div>
              <div style={{ fontSize: roleSize, color: T.t3 }}>{user.role}</div>
            </div>
            {!inline && (
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                style={{
                  transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                  flexShrink: 0,
                }}
              >
                <path d="M2.5 4L5 6.5L7.5 4" fill="none" stroke={T.t3} strokeWidth="1.3" strokeLinecap="round" />
              </svg>
            )}
          </>
        )}
      </div>

      {open && user.onLogout && (
        <div
          style={{
            background: inline ? 'none' : T.surface,
            border: inline ? 'none' : `1px solid ${T.border}`,
            borderRadius: inline ? 0 : 12,
            boxShadow: inline ? 'none' : '0 4px 16px rgba(0,0,0,0.14)',
            zIndex: 100,
            overflow: 'hidden',
            minWidth: inline ? 'auto' : 190,
            ...(inline
              ? { display: 'flex', alignItems: 'center' }
              : {
                  position: 'absolute',
                  ...(iconOnly || compact
                    ? { top: '100%', right: 0, marginTop: 8 }
                    : { bottom: '100%', right: 0, marginBottom: 4 }),
                }),
          }}
        >
          {!inline && (
            <div style={{ padding: '12px 14px 4px' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.t1 }}>{user.name}</div>
              <div style={{ fontSize: 11, color: T.t3, marginTop: 2 }}>{user.role}</div>
            </div>
          )}
          <button
            type="button"
            onClick={() => {
              user.onLogout();
              setOpen(false);
            }}
            style={{
              width: inline ? 'auto' : '100%',
              padding: inline ? '4px 10px' : '10px 14px',
              fontSize: inline ? 11 : 12,
              fontWeight: 600,
              color: T.red,
              background: inline ? T.red + '15' : 'none',
              border: inline ? `1px solid ${T.red}33` : 'none',
              borderTop: inline ? undefined : `1px solid ${T.border}`,
              borderRadius: inline ? 6 : 0,
              cursor: 'pointer',
              textAlign: 'left',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginTop: inline ? 0 : 8,
            }}
          >
            {!inline && <span className="material-symbols-rounded" style={{ fontSize: 16 }}>logout</span>}
            Log out
          </button>
        </div>
      )}
    </div>
  );
}

/** Build initials from full name (e.g. "John Doe" -> "JD"). */
export function getInitials(name: any) {
  if (!name || typeof name !== 'string') return '—';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase().slice(0, 2);
  return name.slice(0, 2).toUpperCase();
}
